import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { PopoverButton } from "@headlessui/react";
import { capitalize } from "lodash-es";
import { useFormContext, useWatch } from "react-hook-form";

import { ManifestJSONSchema } from "src/api/connectApi/manifestTypes";
import { CustomPopover } from "src/base-components/Popover";
import {
  FormPath,
  ManifestFormType,
} from "src/connections/config/manifest/types";
import { Button } from "src/design-system/Button";

export type OptionalObjectProperty = {
  key: string;
  schema: ManifestJSONSchema;
};

type AddObjectFieldDropdownProps = {
  optionalObjects: OptionalObjectProperty[];
  formPath: FormPath;
  prefix: string;
};

// Single "+ Configure" dropdown for all optional object-typed
// properties at this schema level, mirroring the node_inputs AddOptionalField
// UX. Objects that already hold a value are omitted from the list; adding one
// materializes an empty object in the form state, which expands its group.
// Rows are auto-height (unlike AddPopoverRow's fixed h-12) so multi-line
// schema descriptions don't overlap.
export const AddObjectFieldDropdown: React.FC<AddObjectFieldDropdownProps> = ({
  optionalObjects,
  formPath,
  prefix,
}) => {
  const form = useFormContext<ManifestFormType>();
  const currentValues = useWatch({
    control: form.control,
    name: optionalObjects.map(({ key }) => `${formPath}.${key}` as const),
  });

  const addableObjects = optionalObjects.filter(
    (_, index) => currentValues[index] === undefined,
  );

  return (
    <div className="mb-6 flex flex-row">
      <CustomPopover
        button={
          <Button
            dataLoc={`${prefix}-add-object-field`}
            isDisabled={addableObjects.length === 0}
            iconLeft={faPlus}
            iconRight={faChevronDown}
            size="sm"
            variant="secondary"
          >
            Configure
          </Button>
        }
        className="max-h-80"
        placement="bottom-start"
      >
        <div className="flex w-66 flex-col py-2">
          {addableObjects.map(({ key, schema }) => (
            <PopoverButton
              key={key}
              as="button"
              className="px-4 py-2.5 text-left hover:bg-gray-100"
              data-loc={`${prefix}-add-${key}`}
              onClick={() => form.setValue(`${formPath}.${key}`, {})}
            >
              <p className="font-inter-normal-13px text-gray-800">
                {schema.title || capitalize(key)}
              </p>
              {schema.description && (
                <p className="font-inter-normal-12px text-gray-600">
                  {schema.description}
                </p>
              )}
            </PopoverButton>
          ))}
        </div>
      </CustomPopover>
    </div>
  );
};
